import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBag, Upload, Archive, Trash2, Star } from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import MarketplaceDashboardCard from "@/components/MarketplaceDashboardCard";
import { api } from "@/lib/api";

const STATUS_COLOR = { published: "#CCCCCC", draft: "#A3A3A3", archived: "#CCCCCC" };

export default function AdminMarketplace() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.adminMarketplaceList();
            setItems(d.items || []);
        } catch (e) {
            toast.error(e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function toggleFeature(id) {
        try {
            await api.adminMarketplaceToggleFeature(id);
            await load();
        } catch (e) {
            toast.error(e.message);
        }
    }
    async function publish(id) {
        try {
            await api.adminMarketplacePublish(id);
            toast.success("Published");
            await load();
        } catch (e) { toast.error(e.message); }
    }
    async function archive(id) {
        try {
            await api.adminMarketplaceArchive(id);
            toast.success("Archived");
            await load();
        } catch (e) { toast.error(e.message); }
    }
    async function remove(id) {
        if (!window.confirm("Delete asset + all purchases + reviews? Buyer applied entities preserved.")) return;
        try {
            await api.adminMarketplaceDelete(id);
            toast.success("Deleted");
            await load();
        } catch (e) { toast.error(e.message); }
    }

    return (
        <SidebarLayout>
            <div className="mx-auto max-w-6xl">
                <motion.header
                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                >
                    <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                        Admin · Marketplace
                    </p>
                    <h1 className="text-3xl md:text-4xl font-semibold mt-1">Assets</h1>
                    <p className="text-text-secondary mt-2 text-sm">
                        Curate templates, sequences, lead lists, and module unlocks. Buyer-applied
                        entities are preserved even after asset deletion.
                    </p>
                </motion.header>

                <MarketplaceDashboardCard />

                {loading ? (
                    <div className="text-text-secondary text-sm">Loading…</div>
                ) : (
                    <div data-testid="admin-marketplace-grid" className="space-y-3">
                        {items.map((a) => (
                            <div
                                key={a.id}
                                data-testid={`admin-asset-${a.slug}`}
                                className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
                            >
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="min-w-0 flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-xl bg-[var(--color-primary)]/15 grid place-items-center text-[var(--color-primary)]">
                                            <ShoppingBag className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[10px] uppercase tracking-widest" style={{ color: STATUS_COLOR[a.status] }}>
                                                {a.status}{a.featured ? " · FEATURED" : ""}
                                            </p>
                                            <h3 className="font-medium truncate">{a.name}</h3>
                                            <p className="text-xs text-text-secondary mt-0.5">
                                                {a.asset_type} · slug {a.slug} · {a.price_credits_base} cr · plan {a.required_min_plan}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-xs text-text-secondary text-right">
                                        <p>{a.stats?.purchases || 0} purchases · {a.stats?.views || 0} views</p>
                                        {(a.stats?.rating_count || 0) > 0 && (
                                            <p className="mt-0.5 inline-flex items-center gap-1">
                                                <Star className="h-3 w-3 text-[var(--color-primary)]" />
                                                {a.stats.rating_avg.toFixed(1)} · {a.stats.rating_count}
                                            </p>
                                        )}
                                        <p className="mt-0.5">{a.stats?.revenue_credits_lifetime || 0} cr revenue</p>
                                    </div>
                                </div>

                                <p className="text-sm text-text-secondary mt-3 line-clamp-2">{a.description}</p>

                                <div className="mt-4 flex flex-wrap gap-2">
                                    <button
                                        onClick={() => toggleFeature(a.id)}
                                        data-testid={`admin-asset-feature-${a.slug}`}
                                        className="rounded-full border border-[var(--color-primary)]/40 text-[var(--color-primary)] px-3 py-1 text-xs"
                                    >
                                        {a.featured ? "Unfeature" : "Feature"}
                                    </button>
                                    {a.status !== "published" && (
                                        <button
                                            onClick={() => publish(a.id)}
                                            data-testid={`admin-asset-publish-${a.slug}`}
                                            className="inline-flex items-center gap-1 rounded-full bg-[var(--color-success)]/15 border border-[var(--color-success)]/40 text-[var(--color-success)] px-3 py-1 text-xs"
                                        >
                                            <Upload className="h-3 w-3" /> Publish
                                        </button>
                                    )}
                                    {a.status === "published" && (
                                        <button
                                            onClick={() => archive(a.id)}
                                            data-testid={`admin-asset-archive-${a.slug}`}
                                            className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] text-text-secondary px-3 py-1 text-xs"
                                        >
                                            <Archive className="h-3 w-3" /> Archive
                                        </button>
                                    )}
                                    <button
                                        onClick={() => remove(a.id)}
                                        data-testid={`admin-asset-delete-${a.slug}`}
                                        className="inline-flex items-center gap-1 rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] px-3 py-1 text-xs"
                                    >
                                        <Trash2 className="h-3 w-3" /> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </SidebarLayout>
    );
}
